import React from 'react';
import {Routes, Redirect} from '@aboutyou/router';
import {useIsBotModeEnabled} from '@aboutyou/data-fetcher';
import {Category} from './components/pages/CategoryPage/Category';
import {LaunchTeaser} from './components/pages/LaunchTeaserPage/LaunchTeaser';
import {CategoryLegacy} from './components/pages/CategoryPage/CategoryLegacy';
import {ArticleDetailSwitch} from './components/pages/ArticleDetailPage/ArticleDetailSwitch';
import {BasketPage} from './components/pages/BasketPage/BasketPage';
import {HomePage} from './components/pages/HomePage/HomePage';
import {BrandsOverview} from '../bridge/pages/BrandsOverview';
import {BrandShopPage} from './components/pages/BrandPage/BrandShopPage';
import {BrandPageLegacySwitch} from './components/pages/BrandPage/BrandPageLegacySwitch';
import {OutfitDetailPage} from '../bridge/pages/OutfitDetailPage';
import {OutfitOverviewPage} from '../bridge/pages/OutfitOverviewPage';
import {ProductReviewsPage} from '../bridge/pages/ProductReviewsPage';
import {ContactForm} from '../bridge/pages/help-center/ContactForm';
import {AdventsCalendar} from '../bridge/pages/AdventsCalendar';
import {DropsOverviewPage} from '../bridge/pages/DropsOverviewPage';
import {DropDetailPage} from '../bridge/pages/DropDetailPage';
import {SeoStreamPage} from '../bridge/pages/SeoStreamPage';
import {WelcomePage} from '../bridge/pages/WelcomePage';
import {TrackAndTrace} from '../bridge/pages/TrackAndTrace';
import {PremiumPage} from '../bridge/pages/PremiumPage';
import {OrderDetailPage} from '../bridge/pages/account/OrderDetailPage';
import {InitiateReturnPage} from '../bridge/pages/account/InitiateReturnPage';
import {OrdersPage} from '../bridge/pages/account/OrdersPage';
import {PasswordReset} from '../bridge/pages/account/PasswordReset';
import {SearchResults as HelpCenterSearchResult} from '../bridge/pages/help-center/SearchResults';
import {SeoPages} from '../bridge/pages/seo/SeoPages';
import {StoryPage} from '../bridge/pages/StoryPage';
import {StoryOverviewPage} from '../bridge/pages/StoryOverviewPage';
import {OrderSuccessPage} from '../bridge/pages/OrderSuccessPage';
import {ArticlePage as ArticlePageLegacy} from '../bridge/pages/help-center/ArticlePage';
import {SectionPage as SectionPageLegacy} from '../bridge/pages/help-center/SectionPage';
import {LandingPage} from '../bridge/pages/help-center/LandingPage';
import {UserProfile} from '../bridge/pages/account/UserProfile';
import {LegalPage} from '../bridge/pages/LegalPage';
import {CustomerBankData} from '../bridge/pages/account/CustomerBankData';
import {SearchResultsPage} from '../bridge/pages/SearchResultsPage';
import {BlackFriday} from '../bridge/pages/seo/BlackFriday';
import {LiveShoppingPage} from '../bridge/pages/LiveShopping';
import {CustomPage} from './components/pages/CustomPage/CustomPage';
import {SectionPage} from './components/pages/HelpCenter/SectionPage';
import {ArticlePage} from './components/pages/HelpCenter/ArticlePage';
import styled, {css} from 'styled-components';
import {WishlistPage} from './components/pages/WishlistPage/WishlistPage';
import {SubscriptionConfirmationLandingPage} from './components/pages/NewsletterSubscription/SubscriptionConfirmationLandingPage';
import {UnsubscribeLandingPage} from './components/pages/NewsletterSubscription/UnsubscribeLandingPage';
import {UnsubscribeConfirmationLandingPage} from './components/pages/NewsletterSubscription/UnsubscribeConfirmationLandingPage';
import {CheckoutPage} from './components/pages/CheckoutPage/CheckoutPage';
import {SimilarProductsPage} from './components/pages/SimilarProductsPage/SimilarProductsPage';
import {BrandDiscoverPage} from './components/pages/BrandPage/BrandDiscoverPage';
import {NotFound} from './components/pages/NotFoundPage/NotFound';
import {GiftCardRefundPage} from './components/pages/GiftCardRefundPage/GiftCardRefundPage';
import {CampaignConfirmationPage} from './components/pages/CampaignConfirmationPage/CampaignConfirmationPage';
import {CouponLandingPage} from './components/pages/CouponLandingPage/CouponLandingPage';

export const RouteComponents: React.FC = () => {
  const isBotMode = useIsBotModeEnabled();

  return (
    /**
     * The "id=page-content-wrapper" is used to alter the min-height from a page level
     * by adding extra css to this id.
     * This avoids having the PageContentWrapper being moved to page level or passing down
     * callbacks to achieve page specific behavior.
     *
     * @see 'packages/core/application-desktop/src/bridge/pages/Checkout.eager.tsx'
     */
    <PageContentWrapper id="page-content-wrapper" $withMinHeight={!isBotMode}>
      <Routes
        entries={{
          404: {component: <NotFound />},
          account: {component: <Redirect to={'/a/profile'} />},
          accountOverview: {component: <Redirect to={'/a/profile'} />},
          userProfile: {component: <UserProfile />},
          adventsCalendar: {component: <AdventsCalendar />},
          articleDetail: {component: <ArticleDetailSwitch />},
          articleDetailSoldOut: {component: <ArticleDetailSwitch />},
          bankData: {component: <CustomerBankData />},
          basket: {component: <BasketPage />},
          brandDiscover: {component: <BrandDiscoverPage />},
          brandShop: {component: <BrandShopPage />},
          brandLegacy: {component: <BrandPageLegacySwitch />},
          brandsOverview: {component: <BrandsOverview />},
          category: {component: <Category />},
          categoryLegacy: {component: <CategoryLegacy />},
          customPage: {component: <CustomPage />},
          premium: {component: <PremiumPage />},
          checkout: {component: <CheckoutPage />},
          contacts: {component: <ContactForm />},
          couponLandingPage: {
            component: <CouponLandingPage />,
          },
          couponWallet: {component: <HomePage showCouponWallet />},
          dropsOverview: {component: <DropsOverviewPage />},
          dropDetail: {component: <DropDetailPage />},
          favourites: {component: <Redirect to={'/'} />},
          helpCenterArticlePageLegacy: {component: <ArticlePageLegacy />},
          helpCenterLandingPageLegacy: {component: <LandingPage />},
          helpCenterSearchResultPageLegacy: {
            component: <HelpCenterSearchResult />,
          },
          helpCenterSectionPageLegacy: {component: <SectionPageLegacy />},
          helpCenterSectionRedirect: {component: <Redirect to={'/h'} />},
          helpCenterSectionPage: {component: <SectionPage />},
          helpCenterArticlePage: {component: <ArticlePage />},
          home: {component: <HomePage />},
          legal: {component: <LegalPage />},
          notFound: {component: <NotFound />},
          newsletter: {component: <Redirect to={'/'} />},
          newsletterSuccess: {
            component: <Redirect to={'/'} />,
          },
          orderDetails: {component: <OrderDetailPage />},
          orderReturn: {component: <InitiateReturnPage />},
          orderSuccess: {component: <OrderSuccessPage />},
          ordersOverview: {component: <OrdersPage />},
          outfitDetail: {component: <OutfitDetailPage />},
          outfitOverview: {component: <OutfitOverviewPage />},
          passwordReset: {component: <PasswordReset />},
          search: {component: <SearchResultsPage />},
          searchResults: {component: <SearchResultsPage />},
          seoStatic: {component: <SeoPages />},
          seoStream: {component: <SeoStreamPage />},
          settings: {component: <Redirect to={'/a/profile'} />},
          settingsChooseShop: {component: <Redirect to={'/'} />},
          static: {component: <LegalPage />},
          story: {component: <StoryPage />},
          storyOverview: {component: <StoryOverviewPage />},
          subscriptionConfirmation: {
            component: <SubscriptionConfirmationLandingPage />,
          },
          unsubscribeLandingPage: {component: <UnsubscribeLandingPage />},
          unsubscribeConfirmationLandingPage: {
            component: <UnsubscribeConfirmationLandingPage />,
          },
          trackAndTrace: {component: <TrackAndTrace />},
          voucherPage: {component: <Redirect to={'/'} />},
          welcome: {component: <WelcomePage />},
          wishlist: {component: <WishlistPage />},
          productReviews: {component: <ProductReviewsPage />},
          liveShopping: {component: <LiveShoppingPage />},
          teaserPage: {component: <LaunchTeaser />},
          blackFridayPage: {component: <BlackFriday />},
          similarProducts: {component: <SimilarProductsPage />},
          giftCardRefund: {component: <GiftCardRefundPage />},
          campaignConfirmationPage: {component: <CampaignConfirmationPage />},
        }}
      />
    </PageContentWrapper>
  );
};

// Giving a min height with height of Header(+ Campaign Banner)
// This is important to prevent CLS on loading states to push the footer below the fold
const PageContentWrapper = styled.div<{$withMinHeight: boolean}>`
  ${({$withMinHeight}) =>
    $withMinHeight &&
    css`
      min-height: calc(100vh - (188px + 44px));
    `}
`;
